import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useFetcher } from "react-router";
import { BreadCrumbsProvider } from "@made-people/grease-bread-crumbs";
import { StoryblokComponentList } from "@made-people/grease-storyblok";
import { LinkAsLink } from "@made-people/blueprint-link";
import { Cross } from "../../assets/icons/Cross";
import type { LinkAsLinkProps } from "@made-people/blueprint-link";
import type {
  FindPageResponseV1,
  StoryblokPage,
} from "@made-people/nestjs-bff-service";

export interface OpenLinkInModalProps {
  href: string;
  label?: string;
  title?: string;
  variants?: LinkAsLinkProps["variants"];
}

export const OpenLinkInModal = ({
  href,
  label,
  title,
  variants,
}: OpenLinkInModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pageData, setPageData] = useState<StoryblokPage | undefined>(
    undefined,
  );
  const fetcher = useFetcher<{ pageResponse?: FindPageResponseV1 }>();

  const loadLink = useCallback((path: string) => fetcher.load(path), [fetcher]);
  const openModalDialog = useCallback(() => {
    setIsModalOpen(true);
    if (!pageData) {
      loadLink(href);
    }
  }, [href, loadLink, pageData]);

  useEffect(() => {
    if (pageData || !fetcher.data?.pageResponse) {
      return;
    }

    const page = fetcher.data.pageResponse.page;
    if (page.pageType === "storyblokPage") {
      setPageData(page);
    }
  }, [fetcher.data, pageData]);

  return (
    <>
      <LinkAsLink
        link={{
          href,
          onClick: (event) => {
            event.preventDefault();
            openModalDialog();
          },
        }}
        label={label}
        variants={variants}
      />
      <Dialog
        open={isModalOpen}
        onClose={setIsModalOpen}
        className="relative z-[100]"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/40" />
        <div className="fixed inset-0 flex items-center justify-center p-2 lg:p-6">
          <DialogPanel className="relative w-full max-w-[1100px] overflow-hidden rounded-md bg-[#faf9f6] shadow-xl">
            <DialogTitle className="sr-only">
              {title ?? label ?? href}
            </DialogTitle>
            <button
              className="absolute top-4 right-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white"
              onClick={() => setIsModalOpen(false)}
              type="button"
            >
              <Cross className="w-3 h-3" />
            </button>
            <div className="h-full overflow-y-auto">
              {pageData?.storyblokData?.content?.pageContent && (
                <BreadCrumbsProvider breadCrumbs={pageData.breadCrumbs}>
                  <StoryblokComponentList
                    components={pageData.storyblokData.content.pageContent}
                  />
                </BreadCrumbsProvider>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};
